import {
  ENTRA_ID_CODE_CHALLENGE_METHOD,
  ENTRA_ID_RESPONSE_MODE,
  ENTRA_ID_RESPONSE_TYPE,
  ENTRA_ID_SCOPES,
} from '../constantsConfig/constantsConfig';
import { ROUTES } from '../routes/routesConfig';
import * as env from '../services/env';
import { ENTRA_ID_ROUTES } from '../services/requestsService';

import { generateCodeChallenge, generateCodeVerifier, generateNonce, generateState } from './entraIdPkceUtils';

export const ENTRA_ID_TRANSACTION_KEY = 'entra_id_oidc_tx';
export const ENTRA_ID_CONFIG_UNAVAILABLE_MESSAGE = 'Configuração do Entra ID indisponível.';
export const ENTRA_ID_CALLBACK_INVALID_MESSAGE = 'Não foi possível validar o retorno da autenticação.';

const ENTRA_ID_USER_DECLINED_ERRORS = new Set([
  'login_required',
  'interaction_required',
  'consent_required',
  'access_denied',
]);

export interface BuildEntraIdAuthorizeUrlOptions {
  prompt?: 'none' | 'login' | 'select_account';
}

export interface EntraIdTransaction {
  codeVerifier: string;
  nonce: string;
  state: string;
}

export interface EntraIdConfig {
  tenantId: string;
  clientId: string;
}

export const getEntraIdConfig = (): EntraIdConfig => ({
  tenantId: env.getEntraTenantId().trim(),
  clientId: env.getEntraClientId().trim(),
});

export const hasEntraIdConfig = (): boolean => {
  const { tenantId, clientId } = getEntraIdConfig();
  return Boolean(tenantId && clientId);
};

export const isEntraIdUserDeclinedError = (error: string | null | undefined): boolean => {
  return ENTRA_ID_USER_DECLINED_ERRORS.has(String(error ?? '').trim().toLowerCase());
};

export const getEntraIdRedirectUri = (): string => {
  const callbackPath = ROUTES.LOGIN_SSO;
  const pathname = window.location.pathname || callbackPath;
  const loginIndex = pathname.indexOf(callbackPath);
  const basePath = loginIndex >= 0 ? pathname.slice(0, loginIndex) : '';
  return `${window.location.origin}${basePath}${callbackPath}`;
};

export const saveEntraIdTransaction = (transaction: EntraIdTransaction): void => {
  sessionStorage.setItem(ENTRA_ID_TRANSACTION_KEY, JSON.stringify(transaction));
};

export const loadEntraIdTransaction = (): EntraIdTransaction | null => {
  const raw = sessionStorage.getItem(ENTRA_ID_TRANSACTION_KEY);
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as Partial<EntraIdTransaction>;
    if (!parsed.codeVerifier || !parsed.nonce || !parsed.state) {
      return null;
    }
    return {
      codeVerifier: parsed.codeVerifier,
      nonce: parsed.nonce,
      state: parsed.state,
    };
  } catch {
    return null;
  }
};

export const clearEntraIdTransaction = (): void => {
  sessionStorage.removeItem(ENTRA_ID_TRANSACTION_KEY);
};

export const createEntraIdTransaction = async (): Promise<EntraIdTransaction> => ({
  codeVerifier: generateCodeVerifier(),
  nonce: generateNonce(),
  state: generateState(),
});

export const buildEntraIdAuthorizeUrl = async (
  transaction: EntraIdTransaction,
  options?: BuildEntraIdAuthorizeUrlOptions,
): Promise<string> => {
  const { tenantId, clientId } = getEntraIdConfig();
  if (!tenantId || !clientId) {
    throw new Error(ENTRA_ID_CONFIG_UNAVAILABLE_MESSAGE);
  }

  const codeChallenge = await generateCodeChallenge(transaction.codeVerifier);
  const params = new URLSearchParams({
    client_id: clientId,
    response_type: ENTRA_ID_RESPONSE_TYPE,
    redirect_uri: getEntraIdRedirectUri(),
    response_mode: ENTRA_ID_RESPONSE_MODE,
    scope: ENTRA_ID_SCOPES,
    code_challenge: codeChallenge,
    code_challenge_method: ENTRA_ID_CODE_CHALLENGE_METHOD,
    nonce: transaction.nonce,
    state: transaction.state,
  });

  if (options?.prompt) {
    params.set('prompt', options.prompt);
  }

  return `${ENTRA_ID_ROUTES.AUTHORITY_HOST}/${encodeURIComponent(tenantId)}/oauth2/v2.0/authorize?${params.toString()}`;
};

export const validateEntraIdCallback = (code: string, stateFromUrl: string | null): EntraIdTransaction & { code: string } => {
  const stored = loadEntraIdTransaction();
  if (!stored || !code.trim() || !stateFromUrl || stored.state !== stateFromUrl) {
    throw new Error(ENTRA_ID_CALLBACK_INVALID_MESSAGE);
  }

  return {
    code,
    codeVerifier: stored.codeVerifier,
    nonce: stored.nonce,
    state: stored.state,
  };
};
